<template>
  <template v-for="vol in loadedPrevVolumes" :key="`prev-vol-${vol.volNum}`">
    <div
      v-for="(image, imgIndex) in vol.images"
      :key="`prev-vol-${vol.volNum}-img-${imgIndex}`"
      class="comic-image-container"
      :data-page-index="vol.startIndex + imgIndex"
      :data-vol-num="vol.volNum"
    >
      <img :src="image" alt="Comic Page" class="comic-image" />
    </div>
    <ComicReaderInteractPanel
      :vol-num="vol.volNum"
      :interact-data="vol.interactData"
      v-model:comment-input="vol.commentInput"
      :avatar-url="getAvatarUrl()"
      :is-loading-more="isLoadingMoreComments"
      :comment-el-id="commentElId"
      :comment-avatar-url="getCommentAvatarUrl"
      :format-comment-time="formatCommentTime"
      :reply-content-map="replyContentMap"
      :show-reply-box-map="showReplyBoxMap"
      :submitting-reply-map="submittingReplyMap"
      :pending-expand-replies-map="pendingExpandRepliesMap"
      :show-all-replies-map="showAllRepliesMap"
      :toggle-reply-box="toggleReplyBox"
      :submit-comment-reply="(item) => submitVolCommentReply(item, vol)"
      :toggle-comment-replies="(item) => toggleVolCommentReplies(item, vol)"
      :toggle-show-all-replies="toggleShowAllReplies"
      :comment-has-replies="volCommentHasReplies"
      :delete-vol-comment="(item) => deleteVolComment(item, vol)"
      :delete-vol-comment-reply="(parent, reply) => deleteVolCommentReply(parent, reply, vol)"
      @reward="handleReward"
      @like="handleLoadedVolumeLike(vol)"
      @collect="handleLoadedVolumeCollect(vol)"
      @submit-comment="submitLoadedVolumeComment(vol)"
      @comment-like="handleLoadedVolumeCommentLike(vol, $event)"
      @load-more="loadMoreComments(vol)"
      @collapse-comments="collapseComments(vol)"
      @avatar-error="handleAvatarError"
      @comment-avatar-error="handleCommentAvatarError"
    />
  </template>

  <div
    v-for="(image, index) in pages"
    :key="index"
    class="comic-image-container"
    :ref="el => setImageRef(el, index)"
    :data-page-index="index"
    :data-vol-num="volNum"
  >
    <img :src="image" alt="Comic Page" class="comic-image" />
  </div>

  <ComicReaderInteractPanel
    :show="!!pages.length"
    :vol-num="volNum"
    :interact-data="interactData"
    v-model:comment-input="commentInput"
    :avatar-url="getAvatarUrl()"
    :is-loading-more="isLoadingMoreComments"
    :comment-el-id="commentElId"
    :comment-avatar-url="getCommentAvatarUrl"
    :format-comment-time="formatCommentTime"
    :reply-content-map="replyContentMap"
    :show-reply-box-map="showReplyBoxMap"
    :submitting-reply-map="submittingReplyMap"
    :pending-expand-replies-map="pendingExpandRepliesMap"
    :show-all-replies-map="showAllRepliesMap"
    :toggle-reply-box="toggleReplyBox"
    :submit-comment-reply="(item) => submitVolCommentReply(item)"
    :toggle-comment-replies="(item) => toggleVolCommentReplies(item)"
    :toggle-show-all-replies="toggleShowAllReplies"
    :comment-has-replies="volCommentHasReplies"
    :delete-vol-comment="(item) => deleteVolComment(item)"
    :delete-vol-comment-reply="(parent, reply) => deleteVolCommentReply(parent, reply)"
    @reward="handleReward"
    @like="handleLike"
    @collect="handleCollect"
    @submit-comment="submitComment"
    @comment-like="handleCommentLike"
    @load-more="loadMoreComments()"
    @collapse-comments="collapseComments()"
    @avatar-error="handleAvatarError"
    @comment-avatar-error="handleCommentAvatarError"
  />

  <template v-for="vol in loadedVolumes" :key="`loaded-vol-${vol.volNum}`">
    <div
      v-for="(image, imgIndex) in vol.images"
      :key="`loaded-vol-${vol.volNum}-img-${imgIndex}`"
      class="comic-image-container"
      :ref="el => setLoadedVolumeImageRef(el, vol, imgIndex)"
      :data-page-index="vol.startIndex + imgIndex"
      :data-vol-num="vol.volNum"
    >
      <img :src="image" alt="Comic Page" class="comic-image" />
    </div>
    <ComicReaderInteractPanel
      :show="!!(vol.images && vol.images.length)"
      :vol-num="vol.volNum"
      :interact-data="vol.interactData"
      v-model:comment-input="vol.commentInput"
      :avatar-url="getAvatarUrl()"
      :is-loading-more="isLoadingMoreComments"
      :comment-el-id="commentElId"
      :comment-avatar-url="getCommentAvatarUrl"
      :format-comment-time="formatCommentTime"
      :reply-content-map="replyContentMap"
      :show-reply-box-map="showReplyBoxMap"
      :submitting-reply-map="submittingReplyMap"
      :pending-expand-replies-map="pendingExpandRepliesMap"
      :show-all-replies-map="showAllRepliesMap"
      :toggle-reply-box="toggleReplyBox"
      :submit-comment-reply="(item) => submitVolCommentReply(item, vol)"
      :toggle-comment-replies="(item) => toggleVolCommentReplies(item, vol)"
      :toggle-show-all-replies="toggleShowAllReplies"
      :comment-has-replies="volCommentHasReplies"
      :delete-vol-comment="(item) => deleteVolComment(item, vol)"
      :delete-vol-comment-reply="(parent, reply) => deleteVolCommentReply(parent, reply, vol)"
      @reward="handleReward"
      @like="handleLoadedVolumeLike(vol)"
      @collect="handleLoadedVolumeCollect(vol)"
      @submit-comment="submitLoadedVolumeComment(vol)"
      @comment-like="handleLoadedVolumeCommentLike(vol, $event)"
      @load-more="loadMoreComments(vol)"
      @collapse-comments="collapseComments(vol)"
      @avatar-error="handleAvatarError"
      @comment-avatar-error="handleCommentAvatarError"
    />
  </template>

  <div
    v-if="shouldShowNextVolumeLoading()"
    class="load-volume-indicator load-volume-indicator--inline"
  >
    <div class="loading-spinner"></div>
    <span class="loading-text">{{ getLoadingNextVolumeLabel() }}</span>
  </div>

  <div
    v-for="(image, index) in getCurrentVolumeImages()"
    :key="`next-vol-img-${index}`"
    class="comic-image-container"
    :ref="el => setNextVolumeImageRef(el, index)"
    :data-page-index="pages.length + getCurrentVolumeStartIndex() + index"
    :data-vol-num="nextVolumeNum"
  >
    <img :src="image" alt="Comic Page" class="comic-image" />
  </div>

  <div v-if="showLoadNextVolumePrompt || showLoadPrevVolumePrompt" class="load-volume-prompt">
    <div class="load-volume-content">
      <div class="loading-spinner"></div>
      <span class="loading-text">{{ loadVolumePromptText || '正在加载...' }}</span>
    </div>
  </div>

  <div
    v-if="isLoadingPrevVolume && !shouldShowNextVolumeLoading() && !showLoadNextVolumePrompt && !showLoadPrevVolumePrompt"
    class="load-volume-indicator"
  >
    <div class="loading-spinner"></div>
    <span class="loading-text">加载中...</span>
  </div>
</template>

<script setup>
import ComicReaderInteractPanel from './ComicReaderInteractPanel.vue'

defineProps({
  pages: { type: Array, default: () => [] },
  volNum: { type: [String, Number], required: true },
  interactData: { type: Object, default: null },
  loadedPrevVolumes: { type: Array, default: () => [] },
  loadedVolumes: { type: Array, default: () => [] },
  nextVolumeNum: { type: [String, Number, null], default: null },
  showLoadNextVolumePrompt: { type: Boolean, default: false },
  showLoadPrevVolumePrompt: { type: Boolean, default: false },
  loadVolumePromptText: { type: String, default: '' },
  isLoadingPrevVolume: { type: Boolean, default: false },
  commentElId: { type: String, required: true },
  setImageRef: { type: Function, required: true },
  setLoadedVolumeImageRef: { type: Function, required: true },
  setNextVolumeImageRef: { type: Function, required: true },
  getCurrentVolumeImages: { type: Function, required: true },
  getCurrentVolumeStartIndex: { type: Function, required: true },
  shouldShowNextVolumeLoading: { type: Function, required: true },
  getLoadingNextVolumeLabel: { type: Function, required: true },
  getAvatarUrl: { type: Function, required: true },
  getCommentAvatarUrl: { type: Function, required: true },
  formatCommentTime: { type: Function, required: true },
  handleReward: { type: Function, required: true },
  handleLike: { type: Function, required: true },
  handleCollect: { type: Function, required: true },
  submitComment: { type: Function, required: true },
  handleCommentLike: { type: Function, required: true },
  handleLoadedVolumeLike: { type: Function, required: true },
  handleLoadedVolumeCollect: { type: Function, required: true },
  submitLoadedVolumeComment: { type: Function, required: true },
  handleLoadedVolumeCommentLike: { type: Function, required: true },
  loadMoreComments: { type: Function, required: true },
  collapseComments: { type: Function, required: true },
  isLoadingMoreComments: { type: Boolean, default: false },
  handleAvatarError: { type: Function, required: true },
  handleCommentAvatarError: { type: Function, required: true },
  replyContentMap: { type: Object, default: () => ({}) },
  showReplyBoxMap: { type: Object, default: () => ({}) },
  submittingReplyMap: { type: Object, default: () => ({}) },
  pendingExpandRepliesMap: { type: Object, default: () => ({}) },
  showAllRepliesMap: { type: Object, default: () => ({}) },
  toggleReplyBox: { type: Function, required: true },
  submitVolCommentReply: { type: Function, required: true },
  toggleVolCommentReplies: { type: Function, required: true },
  toggleShowAllReplies: { type: Function, required: true },
  volCommentHasReplies: { type: Function, required: true },
  deleteVolComment: { type: Function, required: true },
  deleteVolCommentReply: { type: Function, required: true },
})

const commentInput = defineModel('commentInput', { type: String, default: '' })
</script>
